import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { tap } from 'rxjs';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { ButtonComponent } from '@examdojo/core/button';
import { IconButtonComponent } from '@examdojo/core/icon-button';
import { IconComponent } from '@examdojo/core/icon';
import { NumberInputComponent, TextInputComponent } from '@examdojo/core/input';
import { SingleSelectComponent } from '@examdojo/core/select';
import {
  CanvasEditorComponent,
  CoordinateSystemEditorComponent,
  ControlFieldsEditorComponent,
  ReactionListEditorComponent,
  WIDGET_CONTROL_TYPE,
} from '@examdojo/core/widget';
import type {
  CanvasConfig,
  CoordinateSystemConfig,
  ReactionGroup,
  WidgetControl,
  WidgetSliderControl,
  WidgetButtonControl,
  WidgetDataTableControl,
} from '@examdojo/core/widget';
import type {
  GeometryElementDef,
  GeometryPadDisplayConfig,
  GeometryPadParams,
  GeometryDisplayConfig,
  GeometryElementDisplayConfig,
  LabelScope,
  RightAngleDisplayType,
  AngleUnit,
  AllElementType,
} from '../../geometrypad.model';
import {
  ANGLE_UNITS,
  GEOMETRY_DISPLAY_DEFAULTS,
  GEOMETRYPAD_CANVAS_DEFAULTS,
  LABEL_SCOPES,
  RIGHT_ANGLE_DISPLAY_TYPES,
} from '../../geometrypad.model';
import {
  type GeometryEditorCategory,
  getShapeTypes,
  getAnnotationTypes,
  getFeatureTypes,
  getDefaultElement,
  getElementCategory,
  isShapeElement,
  getAllAvailablePortPaths,
} from '../geometrypad-editor.model';
import { ElementEditorComponent } from './element-editor.component';

interface SelectOption {
  label: string;
  value: string;
}

interface FlatEntry {
  trackId: string;
  id: string;
  displayName: string;
  badge: string;
  category: GeometryEditorCategory | 'control';
  def?: GeometryElementDef;
  display?: GeometryElementDisplayConfig;
  control?: WidgetControl;
  controlIndex?: number;
}

@Component({
  selector: 'dojo-geo-element-list-section',
  standalone: true,
  imports: [
    MatExpansionModule,
    MatSlideToggleModule,
    ReactiveFormsModule,
    ButtonComponent,
    IconButtonComponent,
    IconComponent,
    NumberInputComponent,
    TextInputComponent,
    SingleSelectComponent,
    ElementEditorComponent,
    CanvasEditorComponent,
    CoordinateSystemEditorComponent,
    ControlFieldsEditorComponent,
    ReactionListEditorComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './element-list-section.component.html',
})
export class ElementListSectionComponent {
  constructor(private readonly destroyRef: DestroyRef) {
    effect(() => {
      this.display();
      this.geometryDisplay();
      this.interactivePorts();
      this.syncGeometryDisplayFromConfig();
      this.syncInteractiveFromConfig();
      this.syncPrecisionFromConfig();
    });

    this.addCategoryCtrl.valueChanges
      .pipe(
        tap((val) => this.addCategory.set(val)),
        takeUntilDestroyed(),
      )
      .subscribe();
    this.newEntryTypeCtrl.valueChanges
      .pipe(
        tap((val) => this.selectedAddType.set(val)),
        takeUntilDestroyed(),
      )
      .subscribe();
    this.lengthUnitCtrl.valueChanges
      .pipe(
        tap((val) => this.emitGeometryDisplayChange({ label: { lengthUnit: val || undefined } })),
        takeUntilDestroyed(),
      )
      .subscribe();
    this.defaultPrecisionCtrl.valueChanges
      .pipe(
        tap((val) => {
          const base = this.pendingDisplay ?? this.display() ?? {};
          const result = {
            ...base,
            math: { ...base.math, defaultPrecision: val ?? undefined },
          };
          this.pendingDisplay = result;
          this.displayChange.emit(result);
        }),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  // --- Defaults ---
  readonly canvasDefaults = GEOMETRYPAD_CANVAS_DEFAULTS;

  // --- Inputs ---
  readonly params = input.required<GeometryPadParams>();
  readonly display = input<GeometryPadDisplayConfig>();
  readonly coordSystem = input<CoordinateSystemConfig | undefined>();
  readonly geometryDisplay = input<GeometryDisplayConfig | undefined>();
  readonly behaviorControls = input<WidgetControl[]>([]);
  readonly interactivePorts = input<string[]>([]);
  readonly reactions = input<ReactionGroup[]>([]);

  // --- Outputs ---
  readonly paramsChange = output<GeometryPadParams>();
  readonly displayChange = output<GeometryPadDisplayConfig>();
  readonly coordSystemChange = output<CoordinateSystemConfig>();
  readonly behaviorControlsChange = output<WidgetControl[]>();
  readonly interactivePortsChange = output<string[]>();
  readonly reactionsChange = output<ReactionGroup[]>();

  // --- Pending display state (prevents stale reads on rapid successive changes) ---
  private pendingDisplay: GeometryPadDisplayConfig | null = null;

  // --- Add bar: category selector ---
  readonly addCategoryCtrl = new FormControl<string>('element', { nonNullable: true });
  readonly addCategory = signal<string>('element');

  // --- Add element controls ---
  readonly newEntryTypeCtrl = new FormControl<string>('', { nonNullable: true });
  private readonly selectedAddType = signal('');
  readonly newEntryIdCtrl = new FormControl<string>('', { nonNullable: true });

  // --- Add control controls ---
  readonly newControlTypeCtrl = new FormControl<string>('', { nonNullable: true });
  readonly newControlIdCtrl = new FormControl<string>('', { nonNullable: true });

  // --- Add variable controls ---
  readonly newVarNameCtrl = new FormControl<string>('', { nonNullable: true });
  readonly newVarValueCtrl = new FormControl<number>(0, { nonNullable: true });

  // --- Add interactive port controls ---
  readonly newInteractivePortCtrl = new FormControl<string>('', { nonNullable: true });

  // --- Geometry display controls ---
  readonly shapeColoringCtrl = new FormControl<boolean>(GEOMETRY_DISPLAY_DEFAULTS.shape.coloring, {
    nonNullable: true,
  });
  readonly shapeFillCtrl = new FormControl<boolean>(GEOMETRY_DISPLAY_DEFAULTS.shape.fill, { nonNullable: true });
  readonly angleRightDisplayCtrl = new FormControl<string>(GEOMETRY_DISPLAY_DEFAULTS.angle.rightAngleDisplay, {
    nonNullable: true,
  });
  readonly angleColoringCtrl = new FormControl<boolean>(GEOMETRY_DISPLAY_DEFAULTS.angle.coloring, {
    nonNullable: true,
  });
  readonly angleFillCtrl = new FormControl<boolean>(GEOMETRY_DISPLAY_DEFAULTS.angle.fill, { nonNullable: true });
  readonly labelNamesCtrl = new FormControl<string>('', { nonNullable: true });
  readonly labelValuesCtrl = new FormControl<string>('', { nonNullable: true });
  readonly labelColoringCtrl = new FormControl<boolean>(GEOMETRY_DISPLAY_DEFAULTS.label.coloring, {
    nonNullable: true,
  });
  readonly angleUnitCtrl = new FormControl<string>(GEOMETRY_DISPLAY_DEFAULTS.label.angleUnit, { nonNullable: true });
  readonly lengthUnitCtrl = new FormControl<string>('', { nonNullable: true });

  // --- Precision control ---
  readonly defaultPrecisionCtrl = new FormControl<number | null>(null);

  // --- Per-variable form controls (lazy) ---
  private readonly varNumberCtrls = new Map<string, FormControl<number>>();

  // --- Add category options ---
  readonly addCategoryOptions: SelectOption[] = [
    { label: 'Element', value: 'element' },
    { label: 'Variable', value: 'variable' },
    { label: 'Port', value: 'port' },
    { label: 'Interaction', value: 'interaction' },
  ];

  // --- Add options (split by category) ---
  readonly elementAddOptions: SelectOption[] = [
    ...getShapeTypes().map((t) => ({ label: `${t} \u00B7 shape`, value: `shape:${t}` })),
    ...getAnnotationTypes().map((t) => ({ label: `${t} \u00B7 annotation`, value: `annot:${t}` })),
    ...getFeatureTypes().map((t) => ({ label: `${t} \u00B7 feature`, value: `feat:${t}` })),
  ];

  readonly interactionAddOptions: SelectOption[] = [
    { label: 'Slider', value: 'slider' },
    { label: 'Button', value: 'button' },
    { label: 'Data Table', value: 'data-table' },
    { label: 'Reaction', value: 'reaction' },
  ];

  // --- Geometry display options ---
  readonly rightAngleDisplayOptions: SelectOption[] = RIGHT_ANGLE_DISPLAY_TYPES.map((t) => ({
    label: t.charAt(0).toUpperCase() + t.slice(1),
    value: t,
  }));

  readonly labelScopeOptions: SelectOption[] = LABEL_SCOPES.map((s) => ({
    label: s.charAt(0).toUpperCase() + s.slice(1),
    value: s,
  }));

  readonly angleUnitOptions: SelectOption[] = ANGLE_UNITS.map((u) => ({
    label: u === 'deg' ? 'Degrees (\u00B0)' : u.charAt(0).toUpperCase() + u.slice(1),
    value: u,
  }));

  // --- Computed signals ---

  readonly defaultNewId = computed(() => {
    const raw = this.selectedAddType();
    if (!raw) {
      return '';
    }
    const [, type] = raw.split(':');
    const existingIds = Object.keys(this.params().elements);
    return this.generateId(type, existingIds);
  });

  readonly flatEntries = computed<FlatEntry[]>(() => {
    const params = this.params();
    const displayConfig = this.display();
    const controls = this.behaviorControls();
    const entries: FlatEntry[] = [];

    for (const [id, def] of Object.entries(params.elements)) {
      const category = getElementCategory(def.type as AllElementType);
      entries.push({
        trackId: `el-${id}`,
        id,
        displayName: id,
        badge: def.type,
        category,
        def,
        display: displayConfig?.elements?.[id],
      });
    }

    for (let i = 0; i < controls.length; i++) {
      const ctrl = controls[i];
      entries.push({
        trackId: `ctrl-${ctrl.id}`,
        id: ctrl.id,
        displayName: ctrl.label || ctrl.id,
        badge: ctrl.type,
        category: 'control',
        control: ctrl,
        controlIndex: i,
      });
    }

    const order: Record<string, number> = { shape: 0, feature: 1, annotation: 2, control: 3 };
    entries.sort((a, b) => (order[a.category] ?? 9) - (order[b.category] ?? 9));

    return entries;
  });

  readonly elementEntries = computed(() => this.flatEntries().filter((e) => e.category !== 'control'));
  readonly controlEntries = computed(() => this.flatEntries().filter((e) => e.category === 'control'));

  readonly allElementIds = computed<string[]>(() => Object.keys(this.params().elements));

  readonly shapeElementIds = computed<string[]>(() =>
    Object.entries(this.params().elements)
      .filter(([, def]) => isShapeElement(def))
      .map(([id]) => id),
  );

  readonly variables = computed<Array<[string, number]>>(() => {
    const vars = this.params().variables;
    return vars ? Object.entries(vars) : [];
  });

  readonly interactiveList = computed<string[]>(() => this.interactivePorts());

  readonly allPortPaths = computed<string[]>(() => getAllAvailablePortPaths(this.params().elements));

  readonly portPathOptions = computed<SelectOption[]>(() => {
    const existing = new Set(this.interactivePorts());
    return this.allPortPaths()
      .filter((p) => !existing.has(p))
      .map((p) => ({ label: p, value: p }));
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // Canvas bridge
  // ═══════════════════════════════════════════════════════════════════════════

  onCanvasConfigChange(canvas: CanvasConfig): void {
    const base = this.pendingDisplay ?? this.display() ?? {};
    const result = { ...base, canvas };
    this.pendingDisplay = result;
    this.displayChange.emit(result);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Control bridge
  // ═══════════════════════════════════════════════════════════════════════════

  onControlChange(idx: number, control: WidgetControl): void {
    const controls = [...this.behaviorControls()];
    controls[idx] = control;

    // Propagate variant to all sliders for consistency
    if (control.type === WIDGET_CONTROL_TYPE.Slider) {
      const variant = (control as WidgetSliderControl).variant;
      for (let i = 0; i < controls.length; i++) {
        if (i !== idx && controls[i].type === WIDGET_CONTROL_TYPE.Slider) {
          controls[i] = { ...controls[i], variant } as WidgetControl;
        }
      }
    }

    this.behaviorControlsChange.emit(controls);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Geometry Display
  // ═══════════════════════════════════════════════════════════════════════════

  onShapeColoringChange(value: boolean): void {
    this.emitGeometryDisplayChange({ shape: { coloring: value } });
  }

  onShapeFillChange(value: boolean): void {
    this.emitGeometryDisplayChange({ shape: { fill: value } });
  }

  onRightAngleDisplayChange(value: unknown): void {
    this.emitGeometryDisplayChange({ angle: { rightAngleDisplay: value as RightAngleDisplayType } });
  }

  onAngleColoringChange(value: boolean): void {
    this.emitGeometryDisplayChange({ angle: { coloring: value } });
  }

  onAngleFillChange(value: boolean): void {
    this.emitGeometryDisplayChange({ angle: { fill: value } });
  }

  onLabelNamesChange(value: unknown): void {
    this.emitGeometryDisplayChange({ label: { names: (value || undefined) as LabelScope } });
  }

  onLabelValuesChange(value: unknown): void {
    this.emitGeometryDisplayChange({ label: { values: (value || undefined) as LabelScope } });
  }

  onLabelColoringChange(value: boolean): void {
    this.emitGeometryDisplayChange({ label: { coloring: value } });
  }

  onAngleUnitChange(value: unknown): void {
    this.emitGeometryDisplayChange({ label: { angleUnit: value as AngleUnit } });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Element handling
  // ═══════════════════════════════════════════════════════════════════════════

  onElementParamsChange(elementId: string, partialDef: Partial<GeometryElementDef>): void {
    const currentParams = this.params();
    const currentDef = currentParams.elements[elementId];
    this.paramsChange.emit({
      ...currentParams,
      elements: {
        ...currentParams.elements,
        [elementId]: { ...currentDef, ...partialDef } as GeometryElementDef,
      },
    });
  }

  onElementDisplayChange(elementId: string, newDisplay: GeometryElementDisplayConfig): void {
    const base = this.pendingDisplay ?? this.display() ?? {};
    const result = {
      ...base,
      elements: {
        ...(base.elements ?? {}),
        [elementId]: newDisplay,
      },
    };
    this.pendingDisplay = result;
    this.displayChange.emit(result);
  }

  onDeleteElement(elementId: string): void {
    const currentParams = this.params();
    const { [elementId]: _, ...remaining } = currentParams.elements;
    this.paramsChange.emit({ ...currentParams, elements: remaining });

    const currentDisplay = this.display() ?? {};
    if (currentDisplay.elements?.[elementId]) {
      const { [elementId]: __, ...remainingDisplay } = currentDisplay.elements;
      this.displayChange.emit({ ...currentDisplay, elements: remainingDisplay });
    }
  }

  onDeleteEntry(entry: FlatEntry): void {
    if (entry.category === 'control') {
      const controls = [...this.behaviorControls()];
      controls.splice(entry.controlIndex!, 1);
      this.behaviorControlsChange.emit(controls);
    } else {
      this.onDeleteElement(entry.id);
    }
  }

  onAddElement(): void {
    const raw = this.newEntryTypeCtrl.value;
    if (!raw) {
      return;
    }

    const [category, type] = raw.split(':');
    const customId = this.newEntryIdCtrl.value.trim();
    const existingIds = Object.keys(this.params().elements);
    const id = customId || this.generateId(type, existingIds);
    if (existingIds.includes(id)) {
      return;
    }

    const def = getDefaultElement(type as AllElementType);
    if (category === 'feat' && 'params' in def && this.shapeElementIds().length > 0) {
      (def.params as Record<string, unknown>)['of'] = this.shapeElementIds()[0];
    }

    this.paramsChange.emit({
      ...this.params(),
      elements: { ...this.params().elements, [id]: def },
    });

    this.newEntryTypeCtrl.setValue('', { emitEvent: false });
    this.newEntryIdCtrl.setValue('', { emitEvent: false });
  }

  onAddInteraction(): void {
    const type = this.newControlTypeCtrl.value;
    if (!type) {
      return;
    }

    if (type === 'reaction') {
      const groups = [...(this.reactions() ?? [])];
      groups.push({
        match: [{ when: '', dispatch: { action: 'PATCH_DISPLAY', payload: {} } }],
      });
      this.reactionsChange.emit(groups);
      this.newControlTypeCtrl.setValue('', { emitEvent: false });
      return;
    }

    const customId = this.newControlIdCtrl.value.trim();
    const controls = [...this.behaviorControls()];
    const id = customId || `ctrl-${Date.now()}`;

    if (type === 'slider') {
      controls.push({
        type: WIDGET_CONTROL_TYPE.Slider,
        id,
        label: 'New Slider',
        param: '',
        range: [-10, 10, 0.1],
      } as WidgetSliderControl);
    } else if (type === 'button') {
      controls.push({
        type: WIDGET_CONTROL_TYPE.Button,
        id,
        label: 'New Button',
        trigger: { action: 'PATCH_DISPLAY', payload: {} },
      } as WidgetButtonControl);
    } else if (type === 'data-table') {
      controls.push({
        type: WIDGET_CONTROL_TYPE.DataTable,
        id,
        label: 'New Table',
        columns: [{ key: 'col1', header: 'Column 1' }],
        rows: [{ values: { col1: '' } }],
        position: 'right',
      } as WidgetDataTableControl);
    }
    this.behaviorControlsChange.emit(controls);

    this.newControlTypeCtrl.setValue('', { emitEvent: false });
    this.newControlIdCtrl.setValue('', { emitEvent: false });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Variables
  // ═══════════════════════════════════════════════════════════════════════════

  getVarCtrl(name: string): FormControl<number> {
    if (!this.varNumberCtrls.has(name)) {
      const val = this.params().variables?.[name] ?? 0;
      const ctrl = new FormControl<number>(val, { nonNullable: true });
      this.varNumberCtrls.set(name, ctrl);
      ctrl.valueChanges
        .pipe(
          tap((v) => this.onVariableValueChange(name, v)),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }
    return this.varNumberCtrls.get(name)!;
  }

  onAddVariable(): void {
    const name = this.newVarNameCtrl.value.trim();
    if (!name) {
      return;
    }
    const value = this.newVarValueCtrl.value;
    const currentParams = this.params();
    this.paramsChange.emit({
      ...currentParams,
      variables: { ...(currentParams.variables ?? {}), [name]: value },
    });
    this.newVarNameCtrl.setValue('', { emitEvent: false });
    this.newVarValueCtrl.setValue(0, { emitEvent: false });
  }

  onVariableValueChange(name: string, value: number): void {
    const currentParams = this.params();
    this.paramsChange.emit({
      ...currentParams,
      variables: { ...(currentParams.variables ?? {}), [name]: value },
    });
  }

  onDeleteVariable(name: string): void {
    const currentParams = this.params();
    const { [name]: _, ...remaining } = currentParams.variables ?? {};
    this.varNumberCtrls.delete(name);
    this.paramsChange.emit({
      ...currentParams,
      variables: Object.keys(remaining).length ? remaining : undefined,
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Interactive ports
  // ═══════════════════════════════════════════════════════════════════════════

  onAddInteractivePort(): void {
    const port = this.newInteractivePortCtrl.value.trim();
    if (!port) {
      return;
    }
    const current = this.interactivePorts();
    if (current.includes(port)) {
      return;
    }
    this.interactivePortsChange.emit([...current, port]);
    this.newInteractivePortCtrl.setValue('', { emitEvent: false });
  }

  onInteractivePortSelected(value: unknown): void {
    const port = value as string;
    if (!port) {
      return;
    }
    const current = this.interactivePorts();
    if (current.includes(port)) {
      return;
    }
    this.interactivePortsChange.emit([...current, port]);
    this.newInteractivePortCtrl.setValue('', { emitEvent: false });
  }

  onDeleteInteractivePort(index: number): void {
    const ports = [...this.interactivePorts()];
    ports.splice(index, 1);
    this.interactivePortsChange.emit(ports);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Private
  // ═══════════════════════════════════════════════════════════════════════════

  private syncGeometryDisplayFromConfig(): void {
    this.pendingDisplay = null;
    const geo = this.geometryDisplay();
    this.shapeColoringCtrl.setValue(geo?.shape?.coloring ?? GEOMETRY_DISPLAY_DEFAULTS.shape.coloring, {
      emitEvent: false,
    });
    this.shapeFillCtrl.setValue(geo?.shape?.fill ?? GEOMETRY_DISPLAY_DEFAULTS.shape.fill, { emitEvent: false });
    this.angleRightDisplayCtrl.setValue(
      geo?.angle?.rightAngleDisplay ?? GEOMETRY_DISPLAY_DEFAULTS.angle.rightAngleDisplay,
      { emitEvent: false },
    );
    this.angleColoringCtrl.setValue(geo?.angle?.coloring ?? GEOMETRY_DISPLAY_DEFAULTS.angle.coloring, {
      emitEvent: false,
    });
    this.angleFillCtrl.setValue(geo?.angle?.fill ?? GEOMETRY_DISPLAY_DEFAULTS.angle.fill, { emitEvent: false });
    this.labelNamesCtrl.setValue(geo?.label?.names ?? '', { emitEvent: false });
    this.labelValuesCtrl.setValue(geo?.label?.values ?? '', { emitEvent: false });
    this.labelColoringCtrl.setValue(geo?.label?.coloring ?? GEOMETRY_DISPLAY_DEFAULTS.label.coloring, {
      emitEvent: false,
    });
    this.angleUnitCtrl.setValue(geo?.label?.angleUnit ?? GEOMETRY_DISPLAY_DEFAULTS.label.angleUnit, {
      emitEvent: false,
    });
    this.lengthUnitCtrl.setValue(geo?.label?.lengthUnit ?? '', { emitEvent: false });
  }

  private syncPrecisionFromConfig(): void {
    const dp = this.display()?.math?.defaultPrecision;
    this.defaultPrecisionCtrl.setValue(dp ?? null, { emitEvent: false });
  }

  private syncInteractiveFromConfig(): void {
    // Interactive ports are rendered directly from the input signal
  }

  private emitGeometryDisplayChange(partial: Partial<GeometryDisplayConfig>): void {
    const base = this.pendingDisplay ?? this.display() ?? {};
    const currentGeo = base.geometry ?? {};
    const merged: GeometryDisplayConfig = {
      shape: { ...currentGeo.shape, ...partial.shape },
      angle: { ...currentGeo.angle, ...partial.angle },
      label: { ...currentGeo.label, ...partial.label },
    };
    // Only include keys that have values
    const clean: GeometryDisplayConfig = {};
    if (partial.shape) {
      clean.shape = merged.shape;
    } else {
      clean.shape = currentGeo.shape;
    }
    if (partial.angle) {
      clean.angle = merged.angle;
    } else {
      clean.angle = currentGeo.angle;
    }
    if (partial.label) {
      clean.label = merged.label;
    } else {
      clean.label = currentGeo.label;
    }

    const result = { ...base, geometry: clean };
    this.pendingDisplay = result;
    this.displayChange.emit(result);
  }

  private generateId(type: string, existingIds: string[]): string {
    const baseId = type.charAt(0).toLowerCase();
    let id = baseId;
    let counter = 2;
    while (existingIds.includes(id)) {
      id = `${baseId}${counter}`;
      counter++;
    }
    return id;
  }
}
